import React from "react";
import PropTypes from "prop-types";
import { motion, useInView } from "framer-motion";
import { NavLink } from "react-router-dom";
import { usePadding } from "@/components/utils/Padding";
import useUtils from "@/hooks/useUtils";
import { useRef, useMemo } from "react";
import "./styles.scss";

/* #META
 {
  "NAME": "Services",
  "CLASSNAME": "services-components",
  "URL": "http://nicasource.local"
 }
#ENDMETA */

const Services = ({ data }) => {
  const servicesModule = data?.services_module;
  if (!servicesModule) return null;

  const servicesContent = servicesModule?.services_content;
  const paddingComponent = servicesModule?.padding_component;
  const bottomImage = servicesContent?.image;
  const inner = servicesContent?.inner_content;
  const serviceLabel = servicesContent?.service_label;
  const techLabel = servicesContent?.tech_label;

  const paddingStyle = usePadding(paddingComponent);

  const primary = inner?.primary_tab || null;
  const otherTabs = Array.isArray(inner?.other_tabs) ? inner.other_tabs : [];

  const tabTitleHtml = primary?.tab_title;
  const tabWording = primary?.tab_wording;
  const tabIcon = primary?.tab_icon;

  const pills = useMemo(
    () => (Array.isArray(primary?.tech_pills) ? primary.tech_pills : []),
    [primary]
  );

  const cta = useMemo(() => {
    const link = primary?.cta_button;
    if (link && typeof link === "object" && link.url) return link;
    return null;
  }, [primary]);

  const tabs = [
    { text: primary?.tab_text || "First service", isPrimary: true },
    ...otherTabs.map((t) => ({
      text: t?.tab_text || "",
      link: t?.tab_link && typeof t.tab_link === "object" ? t.tab_link : null,
      isPrimary: false,
    })),
  ].filter((t) => t.text);

  //tabs animation
  const tabsRef = useRef(null);
  const tabsInView = useInView(tabsRef, { once: true, threshold: 0.3 });

  //card animation
  const servicesContentRef = useRef(null);
  const servicesContentInView = useInView(servicesContentRef, { once: true, threshold: 0.3 });

  //anchor link support
  const { convertToRelative, isExternal } = useUtils();

  return (
    <section className="services-component" style={paddingStyle}>
      <div className="container-revamp xl:px-0">
        <motion.div
          ref={servicesContentRef}
          className="services-bottom-content"
          initial={{ opacity: 0, y: 50 }}
          animate={servicesContentInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          <div className="services-bottom-left">
            <div className="services-heading-flex-container">
              <div className="icon-container">
                {tabIcon?.url && (
                  <img
                    src={tabIcon.url}
                    alt={tabIcon.alt || tabIcon.title || "Icon"}
                    className="service-tab-icon max-w-none"
                    loading="lazy"
                    decoding="async"
                    width={80}
                    height={80}
                  />
                )}
              </div>

              {tabTitleHtml && <div className="services-bottom-title">{tabTitleHtml}</div>}
            </div>

            {tabWording && <p className="services-bottom-wording">{tabWording}</p>}

            {/* Tech Pills */}
            <div className="mt-4">
              {pills.length > 0 && (
                <div className="flex flex-col gap-4">
                  <p className="tech-sub-label">{techLabel || "Technologies"}</p>

                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
                    className="pills-wrapper"
                  >
                    {pills.map((tech, i) => {
                      const link =
                        typeof tech?.tech_link === "object" && tech?.tech_link?.url
                          ? tech.tech_link
                          : null;

                      const pillInner = (
                        <>
                          <span className="tech-pill-text">{tech?.tech_name}</span>
                          {tech?.tech_logo && tech.tech_logo.url && (
                            <span className="tech-pill-logo-container">
                              <img
                                src={tech.tech_logo.url}
                                alt={
                                  tech.tech_logo.alt ||
                                  tech.tech_logo.title ||
                                  tech.tech_name ||
                                  "Tech logo"
                                }
                                className="tech-pill-logo"
                                loading="lazy"
                                decoding="async"
                              />
                            </span>
                          )}
                        </>
                      );

                      return link ? (
                        <a
                          key={`pill-${i}`}
                          className="tech-pill has-link"
                          href={link.url}
                          target={link.target || "_self"}
                          rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                        >
                          {pillInner}
                        </a>
                      ) : (
                        <span key={`pill-${i}`} className="tech-pill">
                          {pillInner}
                        </span>
                      );
                    })}
                  </motion.div>
                </div>
              )}

              {pills.length === 0 && (
                <p className="text-light/60 text-sm mt-2">No technologies for this service yet.</p>
              )}

              {cta && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
                  className="button-container"
                >
                  {isExternal(cta.url) ? (
                    <a
                      className="services-button btn-hover-effect"
                      href={cta.url}
                      target={cta.target || "_self"}
                      rel={cta.target === "_blank" ? "noopener noreferrer" : undefined}
                    >
                      {cta.title}
                    </a>
                  ) : (
                    <NavLink
                      to={convertToRelative(cta.url)}
                      className="services-button btn-hover-effect"
                    >
                      {cta.title}
                    </NavLink>
                  )}
                </motion.div>
              )}
            </div>

            {/* Services Tabs */}
            <div className="flex flex-col gap-4">
              {serviceLabel && <p className="services-sub-label">{serviceLabel}</p>}

              {tabs && (
                <div
                  className="services-wrapper"
                  ref={tabsRef}
                  role="tablist"
                  aria-label="Services"
                >
                  {tabs.map((tab, id) => {
                    if (tab.isPrimary) {
                      return (
                        <motion.button
                          key={`tab-${id}`}
                          type="button"
                          aria-current="true"
                          initial={{ opacity: 0, y: 20 }}
                          animate={tabsInView ? { opacity: 1, y: 0 } : {}}
                          transition={{ delay: id * 0.08 }}
                          className="service-tab active"
                        >
                          <span>{tab.text}</span>
                        </motion.button>
                      );
                    }

                    const href = tab.link?.url || "";
                    const isInternal = href && !isExternal(href);

                    return isInternal ? (
                      <NavLink
                        key={`tab-${id}`}
                        to={convertToRelative(href)}
                        initial={{ opacity: 0, y: 20 }}
                        animate={tabsInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: id * 0.08 }}
                        className="service-tab is-link"
                      >
                        <span>{tab.text}</span>
                      </NavLink>
                    ) : (
                      <motion.a
                        key={`tab-${id}`}
                        href={href || "#"}
                        target={tab.link?.target || "_self"}
                        rel={tab.link?.target === "_blank" ? "noopener noreferrer" : undefined}
                        initial={{ opacity: 0, y: 20 }}
                        animate={tabsInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: id * 0.08 }}
                        className="service-tab is-link"
                      >
                        <span>{tab.text}</span>
                      </motion.a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="services-bottom-right">
            {bottomImage?.url && (
              <div className="image-container">
                <img
                  src={bottomImage.url}
                  alt={bottomImage?.alt || bottomImage?.title || ""}
                  className="services-image"
                />
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

Services.propTypes = {
  data: PropTypes.shape({
    acf_fc_layout: PropTypes.string,
    services_module: PropTypes.shape({
      bottom_content: PropTypes.shape({
        content: PropTypes.shape({
          title: PropTypes.string,
          wording: PropTypes.string,
        }),
        image: PropTypes.shape({
          url: PropTypes.string,
          alt: PropTypes.string,
          title: PropTypes.string,
        }),
        service_label: PropTypes.string,
        tech_label: PropTypes.string,
        inner_content: PropTypes.shape({
          primary_tab: PropTypes.shape({
            tab_text: PropTypes.string,
            tab_title: PropTypes.string,
            tab_wording: PropTypes.string,
            tab_icon: PropTypes.shape({
              url: PropTypes.string,
              alt: PropTypes.string,
              title: PropTypes.string,
            }),
            tech_pills: PropTypes.arrayOf(
              PropTypes.shape({
                tech_name: PropTypes.string,
                tech_logo: PropTypes.oneOfType([
                  PropTypes.bool,
                  PropTypes.shape({
                    url: PropTypes.string,
                    alt: PropTypes.string,
                    title: PropTypes.string,
                  }),
                ]),
                tech_link: PropTypes.oneOfType([
                  PropTypes.string,
                  PropTypes.shape({
                    url: PropTypes.string,
                    title: PropTypes.string,
                    target: PropTypes.string,
                  }),
                ]),
              })
            ),
            cta_button: PropTypes.oneOfType([
              PropTypes.string,
              PropTypes.shape({
                title: PropTypes.string,
                url: PropTypes.string,
                target: PropTypes.string,
              }),
            ]),
          }),
          other_tabs: PropTypes.arrayOf(
            PropTypes.shape({
              tab_text: PropTypes.string,
              tab_link: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({
                  url: PropTypes.string,
                  title: PropTypes.string,
                  target: PropTypes.string,
                }),
              ]),
            })
          ),
        }),
      }),
    }),
  }),
};

export default Services;
